import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from "prop-types";
import Fragment from "common/src/components/widget/Fragment";
import Select from "common/src/components/widget/Select";
import Input from "common/src/components/widget/Input";
import Button from "common/src/components/widget/Button";
import {useTranslation} from "react-i18next";
import FloatingLabelTagsWrapper from "common/src/components/widget/FloatingLabelTagsWrapper";
import AggregationsCard from "./AggregationsCard";
import {useDispatch} from "react-redux";
import {addErrorNotification} from "../../../state/notification/notifications";
import {isEqual} from "lodash";
import * as Yup from "yup";
import {useForm} from "common/src/hooks/form/formHook";
import {aggregationOperations} from "../../../model/aggregationOperations";

const SelectedColumns = (props) => {

    const {t} = useTranslation();

    const [selectedColumns, setSelectedColumns] = useState(props.formField.value || []);

    const dispatch = useDispatch();

    const [externalValue, setExternalValue] = useState(props.formField.value || []);

    const previousSelectedColumns = useRef(props.formField.value || []);

    useEffect(() => {
        if (!isEqual(props.formField.value, previousSelectedColumns.current)) {
            setExternalValue(props.formField.value || []);
            setSelectedColumns(props.formField.value || []);

        }
        if (!isEqual(props.formField.value, externalValue)) {
            setExternalValue(props.formField.value || [])
        }
    }, [props.formField.value]);

    useEffect(() => {
        if (!isEqual(previousSelectedColumns.current, selectedColumns)) {
            previousSelectedColumns.current = selectedColumns;
            if (!isEqual(selectedColumns, externalValue)) { // prevent infinite loop
                props.formField.setValue(selectedColumns, undefined, true);
                // setExternalValue(props.formField.value || []);
            }
        }
    }, [selectedColumns, props.restrictedLabels]);
    const isLabelUnique = useCallback((label) => {
            if (!label) return true;

            const isUniqueInSelectedColumns = !selectedColumns.some((column) => column.label === label);
            const isUniqueInRestrictedLabels = !props.restrictedLabels?.some((el) => el === label);

            return isUniqueInSelectedColumns && isUniqueInRestrictedLabels;
        },
        [selectedColumns, props.restrictedLabels]
    );
    useEffect(() => {
        reinitSchema(schema);
    }, [isLabelUnique, selectedColumns, props.restrictedLabels]);
    const schema = {
        label: Yup.string()
            .matches(/^[a-zA-Z0-9_$]+$/, 'Label can only contain letters, numbers, underscores, and dollar signs')
            .matches(/^[^\s]+$/, 'Spaces are not allowed in labels')
            .test('is-unique', 'Label must be unique for columns as well as aggregation aliases', function (value) {
                return isLabelUnique(value);
            })
            .required('Label is required'),
        column: Yup.string().required('Column is required'),
    };
    const {
        values,
        getField,
        handleSubmit: onAdd,
        setFormNotDirty,
        clearValidation,
        reinitSchema
    } = useForm(submit, {}, schema);

    function submit() {
        const newSelectedColumn = {
            name: values.column,
            label: values.label
        };

        if (isLabelUnique(newSelectedColumn.label)) {
            setSelectedColumns([...selectedColumns, newSelectedColumn]);
            setFormNotDirty(true);
            clearValidation();
        } else {
            dispatch(addErrorNotification(
                {
                    title: t(`error.validation.unique.title`),
                    content: t(`error.validation.unique.column.label`),
                }
            ))
        }
    }

    const handleTagRemove = (tag) => {
        setSelectedColumns((columns) =>
            columns.filter(
                (column) => !(column.name === tag.name && column.label === tag.label)
            )
        );
    }
    const isLabelInputDisabled = () => {
        if (!getField("column").value ||
            !props.options ||
            !props.options.find(option => option.name === getField("column").value)) {
            return true;
        }
        return false;
    }
    const options = useMemo(() => {
        let opt = (props.options || []).slice(); // Create a shallow copy of the array
        opt.sort((a, b) => (a != null && b != null && a.name > b.name) ? 1 : -1);
        return opt;
    }, [props.options]);


    return (
        <div>
            <div className="columns  ">
                <div className="column is-full  ">
                    <FloatingLabelTagsWrapper
                        label="Selected columns"
                        labelKeys={[["name"], ["label"]]}
                        labelSeparator=" as "
                        tags={[...selectedColumns]}
                        onTagRemove={handleTagRemove}
                        disabled={props.disabled}
                    />
                </div>
            </div>

            <Fragment rendered={props.options !== undefined && !props.disabled}>
                <div className={"columns mt-3"}>
                    <div className={"column is-4"}>
                        <Select options={options}
                                optionsCode={"name"}
                                optionsLabel={"name"}
                                optionsValue={"name"}
                                emptyOption={true}

                                disabled={options.length === 0}
                                isInvalid={getField("column").error !== undefined && getField("column").error !== ""}
                                invalidMessage={getField("column").error}
                                selectedItem={values.column}
                                label={t('data.column')}
                                onChange={(e) => {
                                    clearValidation();
                                    getField("column").setValue(e, e ? "column" : undefined, false);
                                    getField("label").setValue(e, e ? "label" : undefined, false);
                                }}
                        />
                    </div>
                    <div className={"column is-6"}>
                        <Input label={t('data.label')}
                               disabled={isLabelInputDisabled()}
                               isInvalid={getField("label").error !== undefined && getField("label").error !== ""}
                               invalidMessage={getField("label").error}
                               value={values.label}
                               onChange={(e) => {
                                   clearValidation();
                                   getField("label").setValue(e, ["column", "label"], false);
                               }}
                        />
                    </div>
                    <div className={"column is-2"}>
                        <Button label={t('add')} className={"is-outlined"} onClick={onAdd}/>
                    </div>
                </div>
            </Fragment>
        </div>
    );
}

SelectedColumns.propTypes = {
    options: PropTypes.array,
    formField: PropTypes.object,
    disabled: PropTypes.bool.isRequired,
    restrictedLabels: PropTypes.array
}

AggregationsCard.defaultProps = {
    disabled: false,
    restrictedLabels: []
}

export default SelectedColumns;
